/**
 * Fonte de dados da barra/painel de sync. SOMENTE LEITURA: pendências (pendingData, E1),
 * último envio aceito (circuitBreaker 'push', persistido em localStorage) e último pull
 * (localStorage.lastSyncPull). Cada fonte falha sozinha (null) sem derrubar as outras.
 */
import database from '../database'
import hasInternet from './recognizeInternetConnection'
import {
  getPendingSummary,
  PUSHED_TABLES,
  type PendingSummary,
} from './pendingData'

export type SyncStatusSnapshot = {
  summary: PendingSummary | null
  lastPushOkAt: number | null
  lastPullAt: number | null
  online: boolean
  readAt: number
  /** Quanto durou a leitura (telemetria de custo do poll). */
  readMs: number
}

export type SyncStatusDeps = {
  db?: any
  now?: () => number
  readSummary?: (db: any) => Promise<PendingSummary>
  readLastPush?: () => Promise<number | null>
  readLastPull?: () => number | null
  isOnline?: () => boolean
}

/** ISO, número ou Date.toString() (caminho legado) => epoch ms; ilegível => null. */
export function toEpochMs(v: unknown): number | null {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : Date.parse(String(v))
  return Number.isFinite(n) ? n : null
}

/** Último envio ACEITO (2xx) — circuitBreaker.recordSuccess('push') em sendData. */
export async function readLastPushOkAt(): Promise<number | null> {
  try {
    const { circuitBreaker } = await import('../sync/CircuitBreaker')
    return toEpochMs((circuitBreaker as any).getStatus('push')?.lastSuccess)
  } catch {
    return null
  }
}

export function readLastPullAt(): number | null {
  try {
    return toEpochMs(localStorage.getItem('lastSyncPull'))
  } catch {
    return null
  }
}

const safe = async <T>(fn: () => Promise<T> | T): Promise<T | null> => {
  try {
    return await fn()
  } catch {
    return null
  }
}

export async function readSyncStatusSnapshot(
  deps: SyncStatusDeps = {},
): Promise<SyncStatusSnapshot> {
  const {
    db = database,
    now = Date.now,
    readSummary = getPendingSummary,
    readLastPush = readLastPushOkAt,
    readLastPull = readLastPullAt,
    isOnline = hasInternet,
  } = deps
  const startedAt = now()
  const [summary, lastPushOkAt, lastPullAt] = await Promise.all([
    safe(() => readSummary(db)),
    safe(() => readLastPush()),
    safe(() => readLastPull()),
  ])
  const readAt = now()
  return {
    summary,
    lastPushOkAt,
    lastPullAt,
    online: isOnline(),
    readAt,
    readMs: readAt - startedAt,
  }
}

/**
 * Avisa a cada escrita local nas tabelas enviadas ao servidor (só o sinal, sem o conteúdo).
 * O hook usa para recontar logo depois de salvar, em vez de esperar o próximo poll (QA HML U12).
 * Devolve a função que cancela a inscrição.
 */
export function subscribePendingChanges(
  onChange: () => void,
  db: any = database,
): () => void {
  try {
    return db.experimentalSubscribe([...PUSHED_TABLES], onChange)
  } catch {
    return () => {}
  }
}
